import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useCesiumStore,
  useCustomAlertStore,
  usePointStore,
  useSearchStore,
  useTilesetStore,
  // useUserStore,
} from "../../Store/store";
import {
  GlassmorphismContainer,
  GlassmorphismButton,
} from "../styled/shared/Glassmorphism";
import styled from "styled-components";
import { MapSearch } from "../../api/ProjectAPI";
import HideMenuButton from "./HideMenuButton";
import useIsMobile from "../../hooks/useIsMobile";

const Nav = styled.nav`
  z-index: 1000;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  transition-duration: 500ms;
  transition-property: left, transform;

  @media (max-width: 600px) {
    min-width: 180px;
    top: 0;
    left: 0;
    width: 70%;
    transform: translateX(-100%);

    &.open {
      transform: translateX(0);
    }
  }
`;

export const GlassmorphismPanel = styled.div`
  box-sizing: border-box;
  border-radius: 0;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  padding: 0 30px;
  height: 64px;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  .close-menu-button {
    display: none;
  }

  @media (max-width: 600px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 40px;
    height: 100vh;
    padding: 20px 10px;
    overflow-y: auto;

    .side-bar-header {
      display: flex;
      width: 100%;
      position: relative;

      a {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        width: 65%;
        box-sizing: border-box;
      }

      .close-menu-button {
        box-sizing: border-box;
        width: 35%;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 5px;
        margin-left: auto;
        background-color: transparent;
        border: none;
        color: #333;
      }

      &::after {
        content: "";
        position: absolute;
        width: 2px;
        height: 80%;
        background-color: #e0e0e0;
        right: 35%;
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }
`;

const LogoArea = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const BreadcrumbText = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  font-family: "Paperlogy", sans-serif;
  font-weight: 700;
  font-size: 20px;
  letter-spacing: -0.027em;
  transform: scaleY(1.04) scaleX(1.05);
  transform-origin: left center;
  color: #222;
  white-space: nowrap;

  .separator {
    margin: 0 8px;
    color: #222;
    font-weight: 400;
    letter-spacing: 0;
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  a,
  button {
    color: #333;
    font-size: 14px;
    display: flex;
    gap: 10px;
    padding: 10px 16px;
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;

    img {
      filter: invert(1) brightness(0);
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
    width: 100%;

    a,
    button {
      width: 100%;
      box-sizing: border-box;
      justify-content: flex-start;
    }
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

export default function SideBar({ isMenuOpen, setIsMenuOpen }) {
  const isMobile = useIsMobile(600);
  const { setViwerState, ViwerState, setIsTilesetLoaded } = useTilesetStore();
  const { showWarningAlert, showInfoAlert } = useCustomAlertStore();
  const navigate = useNavigate();
  // const { logout } = useUserStore();
  const { setIsSearchOpen, setOnConfirm, setQuery, setSearchResult } =
    useSearchStore();
  const { clearAllPoints } = useCesiumStore();
  const { setProjectId } = usePointStore();
  const handleViwerState = () => {
    // 검색어 초기화
    setQuery("");
    setSearchResult(null);
    // 타일셋 로드 초기화
    setViwerState(!ViwerState);
    setIsTilesetLoaded(false);
    clearAllPoints && clearAllPoints();
    setProjectId(null);
  };
  const handleSearchButton = () => {
    navigate("/");

    // 검색어 초기화
    setQuery("");

    // 확인 버튼 클릭 시 실행될 함수 설정
    setOnConfirm(async (searchQuery) => {
      // TODO: 여기서 지도 검색 API 호출
      try {
        const response = await MapSearch(searchQuery);
        console.log(response);
        setSearchResult(response);
      } catch (error) {
        // console.error("검색 실패:", error);
        showInfoAlert("검색 실패", "일치하는 결과가 없습니다.");
      }
    });

    // 모달 열기
    setIsSearchOpen(true);
    if (typeof clearAllPoints === "function") {
      clearAllPoints();
    }
  };

  const CloseMenuButtonIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      fill="currentColor"
      className="bi bi-chevron-double-left"
      viewBox="0 0 16 16"
    >
      <path
        fillRule="evenodd"
        d="M8.354 1.646a.5.5 0 0 1 0 .708L2.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"
      />
      <path
        fillRule="evenodd"
        d="M12.354 1.646a.5.5 0 0 1 0 .708L6.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"
      />
    </svg>
  );

  return (
    <>
      {isMobile && isMenuOpen && (
        <Backdrop onClick={() => setIsMenuOpen(false)} />
      )}
      <Nav className={isMenuOpen ? "open" : ""}>
        <GlassmorphismPanel>
          <div className="side-bar-header">
            <LogoArea>
              <Link to="/" onClick={handleViwerState}>
                <img
                  src="/logo.png"
                  alt="logo"
                  style={{ height: "clamp(28px, 5vw, 40px)" }}
                />
              </Link>
              <BreadcrumbText>
                <span className="separator">&gt;</span>
                <span>AI융합서비스</span>
                <span className="separator">&gt;</span>
                <span>자율 농작업 3D 경로 생성 지원 서비스</span>
              </BreadcrumbText>
            </LogoArea>
            <button
              className="close-menu-button"
              onClick={() => setIsMenuOpen(false)}
            >
              {CloseMenuButtonIcon}
            </button>
          </div>
          <ButtonContainer onClick={() => setIsMenuOpen(false)}>
            {/* 검색 버튼 숨김 */}
            <Link to="/" onClick={handleViwerState}>
              <img src="/Icons/globe-americas.svg" alt="globe" width={20} />
              <span>지도 뷰</span>
            </Link>
            <Link to="/project">
              <img src="/Icons/archive.svg" alt="archive" width={20} />
              <span>프로젝트</span>
            </Link>
          </ButtonContainer>
        </GlassmorphismPanel>
      </Nav>
    </>
  );
}
